import React, { useState, useEffect, useCallback } from 'react';

/**
 * LetterFallAdmin
 * ────────────────
 * Admin panel for the Letter Fall game.
 *
 * Three sections:
 *   1. Settings — visibility, premium, name, icon, default script
 *   2. Word picker — search translations DB or "suggest 20 random" + click to add
 *   3. Selected words — list with edit/delete (with confirmation modal)
 *
 * Endpoints: /api/admin/letter-fall/*  (all require admin token)
 *
 * Helpers (TextField, Toggle, etc.) live at MODULE level so inputs keep focus
 * while typing (avoids the React re-render unmount bug we hit earlier).
 */

// ──────────────────────────────────────────────────────────────────────────
// Stable helper components (module-level)
// ──────────────────────────────────────────────────────────────────────────

const fieldClasses = (isDarkMode) => ({
  cardCls: `rounded-xl border p-5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`,
  labelCls: `block text-xs font-semibold uppercase tracking-wider mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`,
  inputCls: `w-full px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`,
  sectionTitleCls: `text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`,
  smallBtnCls: `px-3 py-1.5 rounded-lg text-xs font-semibold ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'}`,
  dangerBtnCls: `px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-500/10 hover:bg-red-500/20 text-red-600 dark:text-red-400 border border-red-500/20`,
  primaryBtnCls: `px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow hover:opacity-95`,
});

const Toggle = ({ k, label, hint, value, onChange, isDarkMode }) => (
  <label className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
    <div className="min-w-0">
      <span className="text-sm font-medium">{label}</span>
      {hint && <div className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{hint}</div>}
    </div>
    <input type="checkbox" checked={!!value} onChange={(e) => onChange(k, e.target.checked)} className="w-4 h-4 flex-shrink-0 ml-3" />
  </label>
);

const TextField = ({ k, label, value, onChange, isDarkMode, type = 'text', placeholder }) => {
  const { labelCls, inputCls } = fieldClasses(isDarkMode);
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <input type={type} className={inputCls} value={value ?? ''} onChange={(e) => onChange(k, e.target.value)} placeholder={placeholder} />
    </div>
  );
};

const RadioGroup = ({ k, label, options, value, onChange, isDarkMode }) => {
  const { labelCls } = fieldClasses(isDarkMode);
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="flex gap-2">
        {options.map(opt => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(k, opt.value)}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
              value === opt.value
                ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white border-transparent'
                : isDarkMode
                  ? 'bg-gray-900 border-gray-700 text-gray-300 hover:bg-gray-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────────────────────

const LetterFallAdmin = ({ API, adminToken, isDarkMode, showNotification }) => {
  const [settings, setSettings] = useState(null);
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Word picker state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addingId, setAddingId] = useState(null);

  // Edit + delete modals
  const [editWord, setEditWord] = useState(null);  // word being edited or null
  const [confirmDelete, setConfirmDelete] = useState(null);

  const { cardCls, labelCls, inputCls, sectionTitleCls, smallBtnCls, dangerBtnCls, primaryBtnCls } = fieldClasses(isDarkMode);

  const authHeaders = { Authorization: `Bearer ${adminToken}` };

  // ─── Load ────────────────────────────────────────────────────────────
  const loadSettings = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/letter-fall/game-settings`, { headers: authHeaders });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setSettings(await r.json());
    } catch (e) {
      showNotification?.(`Failed to load settings: ${e.message}`, 'error');
    }
  }, [API, adminToken, showNotification]);

  const loadWords = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/letter-fall/words`, { headers: authHeaders });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setWords(d.words || []);
    } catch (e) {
      showNotification?.(`Failed to load words: ${e.message}`, 'error');
    }
  }, [API, adminToken, showNotification]);

  useEffect(() => {
    Promise.all([loadSettings(), loadWords()]).finally(() => setLoading(false));
  }, [loadSettings, loadWords]);

  // ─── Settings save ───────────────────────────────────────────────────
  const updateSetting = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const saveSettings = async () => {
    try {
      setSaving(true);
      const r = await fetch(`${API}/admin/letter-fall/game-settings`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });
      if (!r.ok) throw new Error(await r.text());
      const fresh = await r.json();
      setSettings(fresh);
      showNotification?.('Settings saved', 'success');
    } catch (e) {
      showNotification?.(`Save failed: ${e.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  // ─── Word picker ─────────────────────────────────────────────────────
  const doSearch = async (query) => {
    if (!query.trim()) { setSearchResults([]); return; }
    try {
      setSearching(true);
      const r = await fetch(`${API}/admin/letter-fall/search-translations?q=${encodeURIComponent(query)}&limit=20`, { headers: authHeaders });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setSearchResults(d.results || []);
    } catch (e) {
      showNotification?.(`Search failed: ${e.message}`, 'error');
    } finally {
      setSearching(false);
    }
  };

  // Debounce search
  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults([]); return; }
    const id = setTimeout(() => doSearch(searchQuery), 300);
    return () => clearTimeout(id);
  }, [searchQuery]);

  const suggestRandom = async () => {
    try {
      setSearching(true);
      const r = await fetch(`${API}/admin/letter-fall/suggest-random`, {
        method: 'POST',
        headers: authHeaders,
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setSearchResults(d.results || []);
      setSearchQuery('');
      showNotification?.(`Showing ${d.count} random suggestions`, 'success');
    } catch (e) {
      showNotification?.(`Suggest failed: ${e.message}`, 'error');
    } finally {
      setSearching(false);
    }
  };

  // Set of translation IDs already added to the game — used to short-circuit
  // duplicate adds in addWord() and to grey out the "+ Add" button in the
  // search results list.
  const addedIds = React.useMemo(
    () => new Set(words.map((w) => w.source_translation_id).filter(Boolean)),
    [words]
  );

  const addWord = async (translation_id) => {
    if (addedIds.has(translation_id)) {
      const dup = words.find((w) => w.source_translation_id === translation_id);
      showNotification?.(`Already added: "${dup?.english || 'this word'}"`, 'error');
      return;
    }
    try {
      setAddingId(translation_id);
      const r = await fetch(`${API}/admin/letter-fall/words`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ translation_id }),
      });
      if (!r.ok) throw new Error(await r.text());
      // Remove from search results, add to words list
      setSearchResults((r) => r.filter((w) => w.id !== translation_id));
      await loadWords();
      showNotification?.('Word added to game', 'success');
    } catch (e) {
      showNotification?.(`Add failed: ${e.message}`, 'error');
    } finally {
      setAddingId(null);
    }
  };

  // ─── Edit + Delete ───────────────────────────────────────────────────
  const startEdit = (word) => setEditWord({ ...word });
  const cancelEdit = () => setEditWord(null);
  const saveEdit = async () => {
    try {
      const r = await fetch(`${API}/admin/letter-fall/words/${editWord.id}`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          english: editWord.english,
          latin: editWord.latin,
          arabic: editWord.arabic,
        }),
      });
      if (!r.ok) throw new Error(await r.text());
      await loadWords();
      setEditWord(null);
      showNotification?.('Word updated', 'success');
    } catch (e) {
      showNotification?.(`Update failed: ${e.message}`, 'error');
    }
  };

  const askDelete = (word) => setConfirmDelete(word);
  const performDelete = async () => {
    try {
      const r = await fetch(`${API}/admin/letter-fall/words/${confirmDelete.id}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      if (!r.ok) throw new Error(await r.text());
      await loadWords();
      setConfirmDelete(null);
      showNotification?.('Word deleted', 'success');
    } catch (e) {
      showNotification?.(`Delete failed: ${e.message}`, 'error');
    }
  };

  if (loading || !settings) {
    return <div className={`p-8 text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading Letter Fall settings…</div>;
  }

  return (
    <div className="space-y-6 max-w-5xl">

      {/* Sticky header with Save */}
      <div className="flex items-center justify-between sticky top-0 z-10 py-3 -mx-6 px-6 backdrop-blur" style={{ background: isDarkMode ? 'rgba(17,24,39,0.85)' : 'rgba(255,255,255,0.85)' }}>
        <div>
          <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>🔤 Letter Fall</h2>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Pick words from your translations. Players catch falling letters to spell each word.
          </p>
        </div>
        <button
          onClick={saveSettings}
          disabled={saving}
          className="px-5 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold shadow disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save settings'}
        </button>
      </div>

      {/* Settings */}
      <div className={cardCls}>
        <h3 className={sectionTitleCls}>Game Settings</h3>
        <div className="grid md:grid-cols-2 gap-3 mb-3">
          <Toggle k="is_visible" label="Show on Games list" hint="Turn off to hide Letter Fall from the games page." value={settings.is_visible} onChange={updateSetting} isDarkMode={isDarkMode} />
          <Toggle k="is_premium" label="Pro-only (whole game)" hint="Free users see an upgrade screen instead." value={settings.is_premium} onChange={updateSetting} isDarkMode={isDarkMode} />
        </div>
        <div className="grid md:grid-cols-2 gap-3 mb-3">
          <TextField k="name" label="Game name" value={settings.name} onChange={updateSetting} isDarkMode={isDarkMode} />
          <TextField k="icon" label="Icon (emoji)" value={settings.icon} onChange={updateSetting} isDarkMode={isDarkMode} />
        </div>
        <div className="mb-3">
          <TextField k="description" label="Description (shown on game card)" value={settings.description} onChange={updateSetting} isDarkMode={isDarkMode} />
        </div>
        <RadioGroup
          k="default_script"
          label="Default play mode"
          value={settings.default_script}
          onChange={updateSetting}
          isDarkMode={isDarkMode}
          options={[
            { value: 'latin', label: '🔤 Latin (salam)' },
            { value: 'arabic', label: 'ا Arabic (السلام)' },
          ]}
        />
      </div>

      {/* Word picker */}
      <div className={cardCls}>
        <h3 className={sectionTitleCls}>Add Words from Translations</h3>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            placeholder="🔍 Search English word (e.g. hello, house, water)…"
            className={inputCls + ' flex-1'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button onClick={suggestRandom} disabled={searching} className={smallBtnCls + ' whitespace-nowrap disabled:opacity-50'}>
            🎲 Suggest 20 random
          </button>
        </div>
        {searching && (
          <div className={`text-sm text-center py-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Searching…</div>
        )}
        {!searching && searchResults.length > 0 && (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {searchResults.map((w) => (
              <div key={w.id} className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                <div className="min-w-0 flex-1">
                  <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{w.english}</div>
                  <div className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    <span className="font-mono">{w.latin}</span>
                    <span className="mx-2 opacity-50">·</span>
                    <span style={{ fontFamily: "'Noto Naskh Arabic', serif" }}>{w.arabic}</span>
                  </div>
                </div>
                {addedIds.has(w.id) ? (
                  <span
                    className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold ${
                      isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                    }`}
                    title="Already in Selected Words"
                  >
                    ✓ Added
                  </span>
                ) : (
                  <button
                    onClick={() => addWord(w.id)}
                    disabled={addingId === w.id}
                    className={primaryBtnCls + ' flex-shrink-0 disabled:opacity-50'}
                  >
                    {addingId === w.id ? '…' : '+ Add'}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {!searching && searchQuery && searchResults.length === 0 && (
          <div className={`text-sm text-center py-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            No matching verified translations with both Latin + Arabic.
          </div>
        )}
      </div>

      {/* Selected words */}
      <div className={cardCls}>
        <h3 className={sectionTitleCls}>Selected Words ({words.length})</h3>
        {words.length === 0 ? (
          <div className={`text-sm text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            No words yet. Use the search above or "Suggest 20 random" to add some.
          </div>
        ) : (
          <div className="space-y-2">
            {words.map((w) => (
              <div key={w.id} className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                <div className="min-w-0 flex-1">
                  <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{w.english}</div>
                  <div className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    <span className="font-mono">{w.latin}</span>
                    <span className="mx-2 opacity-50">·</span>
                    <span style={{ fontFamily: "'Noto Naskh Arabic', serif" }}>{w.arabic}</span>
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => startEdit(w)} className={smallBtnCls}>✏</button>
                  <button onClick={() => askDelete(w)} className={dangerBtnCls}>🗑</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editWord && (
        <div className="fixed inset-0 z-[3000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={cancelEdit}>
          <div className={`relative w-full max-w-md rounded-2xl shadow-2xl border ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`} onClick={(e) => e.stopPropagation()}>
            <div className={`flex items-center gap-3 p-5 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
              <h3 className={`text-lg font-extrabold flex-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Edit word</h3>
              <button onClick={cancelEdit} className={`text-xl ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}>×</button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className={labelCls}>English</label>
                <input type="text" className={inputCls} value={editWord.english} onChange={(e) => setEditWord({ ...editWord, english: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Latin script</label>
                <input type="text" className={inputCls + ' font-mono'} value={editWord.latin} onChange={(e) => setEditWord({ ...editWord, latin: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Arabic script</label>
                <input type="text" className={inputCls} style={{ fontFamily: "'Noto Naskh Arabic', serif" }} value={editWord.arabic} onChange={(e) => setEditWord({ ...editWord, arabic: e.target.value })} />
              </div>
            </div>
            <div className={`flex items-center justify-end gap-2 px-5 py-4 border-t ${isDarkMode ? 'border-gray-800 bg-gray-900/60' : 'border-gray-100 bg-gray-50'} rounded-b-2xl`}>
              <button onClick={cancelEdit} className={`px-4 py-2 rounded-lg text-sm font-bold ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-900'}`}>Cancel</button>
              <button onClick={saveEdit} className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 text-white text-sm font-bold">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[3000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setConfirmDelete(null)}>
          <div className={`relative w-full max-w-md rounded-2xl shadow-2xl border ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`} onClick={(e) => e.stopPropagation()}>
            <div className={`flex items-center gap-3 p-5 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
              <div className="w-10 h-10 rounded-full bg-red-500/15 text-red-500 flex items-center justify-center text-xl font-bold">⚠</div>
              <div className="flex-1">
                <h3 className={`text-lg font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Remove this word?</h3>
                <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Removes from Letter Fall only. Translation stays in your dictionary.</p>
              </div>
            </div>
            <div className="p-5">
              <div className={`p-4 rounded-lg border text-sm ${isDarkMode ? 'border-gray-700 bg-gray-800/40 text-gray-200' : 'border-gray-200 bg-gray-50 text-gray-800'}`}>
                <div className="font-semibold">{confirmDelete.english}</div>
                <div className="mt-1 text-xs opacity-80">
                  <span className="font-mono">{confirmDelete.latin}</span> · <span style={{ fontFamily: "'Noto Naskh Arabic', serif" }}>{confirmDelete.arabic}</span>
                </div>
              </div>
            </div>
            <div className={`flex items-center justify-end gap-2 px-5 py-4 border-t ${isDarkMode ? 'border-gray-800 bg-gray-900/60' : 'border-gray-100 bg-gray-50'} rounded-b-2xl`}>
              <button onClick={() => setConfirmDelete(null)} className={`px-4 py-2 rounded-lg text-sm font-bold ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-900'}`}>Cancel</button>
              <button onClick={performDelete} className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold">🗑 Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LetterFallAdmin;
